import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { ScheduleJobView } from "@/hooks/useScheduleJobs";

/**
 * `ScheduleJobDialog` — create / edit a scheduled job (agendamentos plan P2;
 * R2 handoff §3.2), in the masterplan §3.7 grammar: benefit-led header, then
 * the fields, then the RESTATEMENT confirm.
 *
 * Three rules from the spec are load-bearing:
 *  1. **Kind = `reminder` + `task` ONLY** (P0 ruling 1 — `watch` is
 *     creatable-but-unarmed until R4, so it is NOT offered here).
 *  2. **Structured schedule only — never a raw cron field.** One-shot /
 *     every-N / daily (+ dias úteis) / weekly, exactly the shapes
 *     `schedule.create` accepts.
 *  3. **The restatement IS the approval.** Before writing we show the lawyer
 *     one sentence describing what will happen on a recurring basis, and send
 *     that same sentence as `restatement` — it is recorded on the job as the
 *     standing approval that legitimizes every later unattended run (§5.2).
 *
 * Runtime errors (`INVALID_SCHEDULE`, `INTERVAL_FLOOR`, `JOB_CAP`, …) carry
 * user-facing PT-BR messages — surfaced VERBATIM, never re-worded.
 */

type JobKind = "reminder" | "task";
type ScheduleShape = "at" | "every" | "daily" | "weekly";

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
type Weekday = (typeof WEEKDAYS)[number];

/** The runtime's floor (handoff §2.3 `INTERVAL_FLOOR`) — mirrored client-side
 *  so the lawyer is told BEFORE a refused round-trip. The server still owns
 *  the verdict; its message wins when they disagree. */
const INTERVAL_FLOOR_MIN = 15;

export function ScheduleJobDialog({
  job,
  onClose,
  onSaved,
}: {
  /** `undefined` = create; a job = edit (its shape seeds the form). */
  job?: ScheduleJobView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const isEdit = !!job;

  const [title, setTitle] = useState(job?.title ?? "");
  const [kind, setKind] = useState<JobKind>(
    job?.kind === "task" ? "task" : "reminder",
  );
  const [body, setBody] = useState(
    job?.spec.kind === "reminder"
      ? job.spec.text
      : job?.spec.kind === "task"
        ? job.spec.instruction
        : "",
  );
  const [shape, setShape] = useState<ScheduleShape>(() => {
    if (!job) return "daily";
    if (job.schedule.kind === "at") return "at";
    if (job.schedule.kind === "every") return "every";
    // A cron-backed row is daily/weekly upstream; default to daily and let the
    // lawyer re-pick rather than parse an expression we must never re-derive.
    return "daily";
  });
  const [at, setAt] = useState("");
  const [everyMinutes, setEveryMinutes] = useState(
    job?.schedule.kind === "every"
      ? String(Math.max(INTERVAL_FLOOR_MIN, Math.round(job.schedule.everyMs / 60000)))
      : "60",
  );
  const [time, setTime] = useState("08:00");
  const [weekday, setWeekday] = useState<Weekday>("mon");
  const [skipWeekends, setSkipWeekends] = useState(
    job?.schedule.kind === "cron" ? !!job.schedule.skipWeekends : false,
  );
  const [deliver, setDeliver] = useState<"owner" | "none">(
    job?.delivery.mode === "none" ? "none" : "owner",
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  /** The schedule payload exactly as `schedule.create` wants it. */
  const schedulePayload = useMemo(() => {
    switch (shape) {
      case "at":
        return { kind: "at" as const, at };
      case "every":
        return { kind: "every" as const, everyMinutes: Number(everyMinutes) || 0 };
      case "daily":
        return { kind: "daily" as const, time, skipWeekends };
      case "weekly":
        return { kind: "weekly" as const, time, weekday };
    }
  }, [shape, at, everyMinutes, time, skipWeekends, weekday]);

  /** One human sentence describing the standing arrangement — shown to the
   *  lawyer AND sent as `restatement` (they must be the same words). */
  const restatement = useMemo(() => {
    const cadence =
      shape === "at"
        ? t("schedule.dialog.restate.at", { at: at || "—" })
        : shape === "every"
          ? t("schedule.dialog.restate.every", { minutes: everyMinutes })
          : shape === "daily"
            ? skipWeekends
              ? t("schedule.dialog.restate.dailyWeekdays", { time })
              : t("schedule.dialog.restate.daily", { time })
            : t("schedule.dialog.restate.weekly", {
                time,
                weekday: t(`schedule.weekday.${weekday}`),
              });
    const action =
      kind === "reminder"
        ? t("schedule.dialog.restate.actionReminder", { text: body || "—" })
        : t("schedule.dialog.restate.actionTask", { instruction: body || "—" });
    const delivery =
      deliver === "owner"
        ? t("schedule.dialog.restate.deliverOwner")
        : t("schedule.dialog.restate.deliverNone");
    return `${cadence} ${action} ${delivery}`;
  }, [shape, at, everyMinutes, time, skipWeekends, weekday, kind, body, deliver, t]);

  const belowFloor =
    shape === "every" && Number(everyMinutes) > 0 && Number(everyMinutes) < INTERVAL_FLOOR_MIN;
  const canSave = !!title.trim() && !!body.trim() && !belowFloor && !saving;

  async function save() {
    if (!rpc || !canSave) return;
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        kind,
        schedule: schedulePayload,
        deliver,
        restatement,
      };
      if (kind === "reminder") payload.text = body.trim();
      else payload.instruction = body.trim();

      if (isEdit) {
        await rpc.call("schedule.update", { job: { ...payload, jobId: job.id } });
      } else {
        await rpc.call("schedule.create", { job: payload });
      }
      onSaved();
      onClose();
    } catch (err) {
      // The runtime's message is already user-facing PT-BR — verbatim (§2.3).
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const field = "w-full rounded border border-cream-300 bg-transparent px-2 py-1.5 text-sm dark:border-coffee-700";
  const label = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-coffee-600 dark:text-cream-400";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="schedule-job-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-coffee-900"
      >
        {/* §3.7 — benefit first, mechanism after. */}
        <div className="space-y-1.5 border-b border-gold-400/50 bg-gold-400/10 px-6 py-5 dark:border-gold-500/30 dark:bg-gold-500/10">
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 font-serif-display text-xl leading-snug text-coffee-900 dark:text-cream-100">
              {t(isEdit ? "schedule.dialog.editTitle" : "schedule.dialog.createTitle")}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              title={t("common.close")}
              data-testid="schedule-job-dialog-close"
              className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-white/60 hover:text-coffee-800 dark:text-cream-400 dark:hover:bg-coffee-800"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-body-sm text-coffee-700 dark:text-cream-300">
            {t("schedule.dialog.blurb")}
          </p>
        </div>

        <div className="space-y-3 px-6 py-4">
          <div>
            <span className={label}>{t("schedule.dialog.kindLabel")}</span>
            <div className="flex gap-2">
              {(["reminder", "task"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`schedule-kind-${k}`}
                  aria-pressed={kind === k}
                  onClick={() => setKind(k)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    kind === k
                      ? "border-gold-500 bg-gold-400/20 text-gold-700 dark:text-gold-400"
                      : "border-cream-300 text-coffee-600 hover:bg-cream-100 dark:border-coffee-700 dark:text-cream-400 dark:hover:bg-coffee-800",
                  )}
                >
                  {t(`schedule.kind.${k}`)}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-coffee-500 dark:text-cream-500">
              {t(`schedule.dialog.kindHint.${kind}`)}
            </p>
          </div>

          <label className="block">
            <span className={label}>{t("schedule.dialog.titleLabel")}</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="schedule-title-input"
              className={field}
              placeholder={t("schedule.dialog.titlePlaceholder")}
            />
          </label>

          <label className="block">
            <span className={label}>
              {t(kind === "reminder" ? "schedule.dialog.textLabel" : "schedule.dialog.instructionLabel")}
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              data-testid="schedule-body-input"
              className={field}
              placeholder={t(
                kind === "reminder"
                  ? "schedule.dialog.textPlaceholder"
                  : "schedule.dialog.instructionPlaceholder",
              )}
            />
          </label>

          {/* STRUCTURED schedule — deliberately no raw-cron field anywhere. */}
          <div>
            <span className={label}>{t("schedule.dialog.whenLabel")}</span>
            <div className="mb-2 flex flex-wrap gap-2">
              {(["at", "every", "daily", "weekly"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`schedule-shape-${s}`}
                  aria-pressed={shape === s}
                  onClick={() => setShape(s)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    shape === s
                      ? "border-gold-500 bg-gold-400/20 text-gold-700 dark:text-gold-400"
                      : "border-cream-300 text-coffee-600 hover:bg-cream-100 dark:border-coffee-700 dark:text-cream-400 dark:hover:bg-coffee-800",
                  )}
                >
                  {t(`schedule.shape.${s}`)}
                </button>
              ))}
            </div>

            {shape === "at" && (
              <input
                type="datetime-local"
                value={at}
                onChange={(e) => setAt(e.target.value.replace("T", " "))}
                data-testid="schedule-at-input"
                className={field}
              />
            )}
            {shape === "every" && (
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={INTERVAL_FLOOR_MIN}
                    value={everyMinutes}
                    onChange={(e) => setEveryMinutes(e.target.value)}
                    data-testid="schedule-every-input"
                    className={cn(field, "w-28", belowFloor && "border-red-400")}
                  />
                  <span className="text-sm text-coffee-600 dark:text-cream-400">
                    {t("schedule.dialog.minutes")}
                  </span>
                </div>
                {belowFloor && (
                  <p
                    className="mt-1 text-[11px] text-red-600 dark:text-red-300"
                    data-testid="schedule-floor-hint"
                  >
                    {t("schedule.dialog.floorHint", { min: INTERVAL_FLOOR_MIN })}
                  </p>
                )}
              </div>
            )}
            {(shape === "daily" || shape === "weekly") && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  data-testid="schedule-time-input"
                  className={cn(field, "w-32")}
                />
                {shape === "weekly" && (
                  <select
                    value={weekday}
                    onChange={(e) => setWeekday(e.target.value as Weekday)}
                    data-testid="schedule-weekday-select"
                    className={cn(field, "w-auto")}
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d} value={d}>
                        {t(`schedule.weekday.${d}`)}
                      </option>
                    ))}
                  </select>
                )}
                {shape === "daily" && (
                  <label className="flex items-center gap-1.5 text-xs text-coffee-600 dark:text-cream-400">
                    <input
                      type="checkbox"
                      checked={skipWeekends}
                      onChange={(e) => setSkipWeekends(e.target.checked)}
                      data-testid="schedule-skip-weekends"
                    />
                    {t("schedule.dialog.skipWeekends")}
                  </label>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-coffee-600 dark:text-cream-400">
            <input
              type="checkbox"
              checked={deliver === "owner"}
              onChange={(e) => setDeliver(e.target.checked ? "owner" : "none")}
              data-testid="schedule-deliver-toggle"
            />
            {t("schedule.dialog.deliverLabel")}
          </label>

          {/* THE RESTATEMENT — the standing approval, shown and sent verbatim. */}
          <div
            className="rounded-lg border border-cream-300/80 bg-cream-50 p-3 dark:border-coffee-700 dark:bg-coffee-950/50"
            data-testid="schedule-restatement"
          >
            <span className={label}>{t("schedule.dialog.restatementLabel")}</span>
            <p className="text-body-sm text-coffee-800 dark:text-cream-200">{restatement}</p>
          </div>

          {error && (
            <p
              className="rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300"
              data-testid="schedule-dialog-error"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-cream-200/70 pt-3 dark:border-coffee-800/60">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-body-sm text-coffee-600 transition-colors hover:bg-cream-100 dark:text-cream-400 dark:hover:bg-coffee-800"
            >
              {t("common.cancel", "Cancelar")}
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!canSave}
              data-testid="schedule-dialog-confirm"
              className="inline-flex items-center gap-1.5 rounded-md bg-gold-500 px-4 py-1.5 text-body-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400 disabled:opacity-50"
            >
              {saving && <Loader2 size={12} aria-hidden className="animate-spin" />}
              {t("schedule.dialog.confirmButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
